import { Component, ViewChild, computed, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { AppMatAutocompleteComponent, XmedicalUiModule } from '@xmedical/ui';
import { Allergy, PipesModule } from '@xmedical/core';
import { WithReferralInfo, hasReferralInfo, formatReferralSource } from '../../types/referral-info';

/**
 * Generic interface for displayed allergy items.
 * Can be either a full Allergy or a patient-specific allergy record.
 */
export interface AllergyDisplayItem extends WithReferralInfo {
  id?: number;
  allergy: string;
  comment?: string;
  lastModifiedDate?: Date;
  modifiedDateUtc?: Date;
  lastModifiedUser?: { firstName?: string; surname?: string };
}

/**
 * Event emitted when an allergy is added from search results.
 */
export interface AllergyAddedEvent {
  item: Allergy;
  comment: string;
}

/**
 * Event emitted when an allergy is removed from the list.
 */
export interface AllergyRemovedEvent {
  item: AllergyDisplayItem;
  index: number;
}

@Component({
  selector: 'xm-allergy-widget-view',
  templateUrl: './allergy-widget-view.component.html',
  styleUrls: ['./allergy-widget-view.component.scss'],
  imports: [
    DatePipe,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    XmedicalUiModule,
    PipesModule
  ]
})
export class AllergyWidgetViewComponent {
  @ViewChild(AppMatAutocompleteComponent) autocomplete!: AppMatAutocompleteComponent;

  // Inputs
  items = input<AllergyDisplayItem[]>([]);
  title = input<string>('Allergies');
  searchFn = input.required<(query: string) => Observable<Allergy[]>>();
  isProcessing = input<boolean>(false);
  canAdd = input<boolean>(true);
  canDelete = input<boolean>(true);
  addItemText = input<string>('Add new allergy');
  usePrimaryText = input<boolean>(false);

  // Outputs
  itemAdded = output<AllergyAddedEvent>();
  itemRemoved = output<AllergyRemovedEvent>();
  addNewItem = output<string>();

  // Track comments for search results before adding
  resultComments: { [key: number]: string } = {};

  // Computed signal for reactive hasItems check
  hasItems = computed(() => this.items().length > 0);

  getResultComment(itemId: number): string {
    return this.resultComments[itemId] || '';
  }

  setResultComment(itemId: number, comment: string): void {
    this.resultComments[itemId] = comment;
  }

  onItemSelected(result: Allergy): void {
    const comment = this.getResultComment(result.id);
    this.itemAdded.emit({ item: result, comment });
    this.clearSearch();
  }

  onRemoveItem(item: AllergyDisplayItem, index: number): void {
    this.itemRemoved.emit({ item, index });
  }

  onAddNewItem(): void {
    const searchTerm = this.autocomplete?.queryText || '';
    this.clearSearch();  // Close search panel
    this.addNewItem.emit(searchTerm);
  }

  private clearSearch(): void {
    this.resultComments = {};
    if (this.autocomplete) {
      this.autocomplete.cancelSearch();
    }
  }

  // Referral helper methods for template
  isReferred(item: AllergyDisplayItem): boolean {
    return hasReferralInfo(item);
  }

  getReferralText(item: AllergyDisplayItem): string {
    return formatReferralSource(item);
  }
}
